import React, { useMemo, useState } from 'react';
import { Search, Plus, Pencil, UserPlus, AlertTriangle, Info, UserX, RotateCcw } from 'lucide-react';
import {
  AdminEmployeeListItem, AdminRoleWithUsage, EmployeeLite,
  AdminEmployeeWriteInput, createAdminEmployee, updateAdminEmployee,
  setAdminEmployeeActive, resetAdminEmployeeSignIn, getApiErrorMessage,
} from '../../../api/adminManagement';
import { AdminEmployeeRole } from '../../../interfaces/EmployeeInfoInterface';
import {
  Avatar, StatusPill, SuperAdminMarker, CountChip, InlineError, ErrorBanner,
  WarnBanner, SearchablePicker, PickerOption, ModalShell,
} from './AdminManagementKit';

const LAST_SUPER_ADMIN_MSG = 'At least one active Super Admin is required';

type StatusFilter = 'active' | 'inactive' | 'all';

/** Role filter: a role id, every role, or rows with no admin role (employee-only, TAS-667). */
type RoleFilter = number | 'all' | 'employeeOnly';

/** <select> value for the "No admin role — employee access only" option. */
const EMPLOYEE_ONLY_VALUE = 'employee-only';
const EMPLOYEE_ONLY_SUMMARY =
  'Employee only · signs in to the employee time portal. No admin pages, approvals or employee scope.';
const EMPLOYEE_LINK_REQUIRED_MSG = 'Link an employee: employee-only access needs one.';

/** Server 400s about the linked employee (TAS-667 link validation) go under the picker. */
const EMPLOYEE_LINK_ERROR_RE = /^Employee \d+ |employee link is required/i;

/** One-line role summary shown under the Role dropdown in the Add/Edit modal. */
function roleSummary(role: AdminEmployeeRole | undefined): string {
  if (!role) return '';
  if (role.admin) return 'Super admin · full access to everything, everywhere.';
  const scope = role.supervisor
    ? 'Supervisor scope'
    : role.viewAllEmployees
      ? 'Org-wide visibility'
      : 'No employee scope';
  const caps: string[] = [];
  if (role.approveTimesheets || role.payrollApproveTimesheets) caps.push('approves timesheets');
  else if (role.viewTimesheets) caps.push('views timesheets');
  if (role.approveDenyTimeOffRequests) caps.push('time off');
  if (role.processPayroll) caps.push('processes payroll');
  if (role.viewReports && caps.length < 2) caps.push('reports');
  const capText = caps.length ? caps.join(' & ') : 'view-only / back-office';
  return `${scope} · ${capText}.`;
}

interface FormState {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  altEmployeeID: string;
  employeeID: number | null;
  /** Null with employeeOnly=false means no choice made yet. */
  adminEmployeeRoleID: number | null;
  /** "No admin role — employee access only" chosen (TAS-667). */
  employeeOnly: boolean;
}

const EMPTY_FORM: FormState = {
  firstName: '', middleName: '', lastName: '', email: '',
  altEmployeeID: '', employeeID: null, adminEmployeeRoleID: null, employeeOnly: false,
};

interface Props {
  admins: AdminEmployeeListItem[];
  roles: AdminRoleWithUsage[];
  employees: EmployeeLite[];
  currentAdminEmployeeID: number | null;
  onReload: () => void;
  onDelegationChip: (admin: AdminEmployeeListItem) => void;
  showToast: (msg: string, isError?: boolean) => void;
}

const AdminEmployeesTab: React.FC<Props> = ({
  admins, roles, employees, currentAdminEmployeeID, onReload, onDelegationChip, showToast,
}) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AdminEmployeeListItem | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [employeeError, setEmployeeError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Inactivate confirm
  const [confirmTarget, setConfirmTarget] = useState<AdminEmployeeListItem | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  // Reset sign-in confirm (TAS-667)
  const [resetTarget, setResetTarget] = useState<AdminEmployeeListItem | null>(null);
  const [resetBusy, setResetBusy] = useState(false);

  // ── Lookups ────────────────────────────────────────────────────────────────
  const employeeName = useMemo(() => {
    const m = new Map<number, string>();
    employees.forEach((e) => m.set(e.employeeID, `${e.firstName} ${e.lastName}`.trim()));
    return m;
  }, [employees]);

  const employeePickerOptions: PickerOption[] = useMemo(
    () => employees
      .filter((e) => e.active)
      .map((e) => ({
        id: e.employeeID,
        name: `${e.firstName} ${e.lastName}`.trim(),
        sub: e.altEmployeeID ? `#${e.altEmployeeID}` : undefined,
      })),
    [employees],
  );

  const activeRoles = useMemo(() => roles.filter((r) => r.active), [roles]);

  const activeSuperAdminCount = useMemo(
    () => admins.filter((a) => a.active && a.roleIsSuperAdmin).length,
    [admins],
  );

  // ── Guard predicates (mirror the server; server is authoritative) ────────────
  const isSelf = (a: AdminEmployeeListItem) => a.adminEmployeeID === currentAdminEmployeeID;
  const isLastActiveSuperAdmin = (a: AdminEmployeeListItem) =>
    a.active && a.roleIsSuperAdmin && activeSuperAdminCount === 1;

  // ── Filtering ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return admins.filter((a) => {
      if (statusFilter === 'active' && !a.active) return false;
      if (statusFilter === 'inactive' && a.active) return false;
      if (roleFilter === 'employeeOnly' && a.adminEmployeeRoleID !== null) return false;
      if (typeof roleFilter === 'number' && a.adminEmployeeRoleID !== roleFilter) return false;
      if (q) {
        const name = `${a.name.first} ${a.name.middle ?? ''} ${a.name.last}`.toLowerCase();
        if (!name.includes(q) && !(a.email ?? '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [admins, search, statusFilter, roleFilter]);

  // ── Modal open/close ─────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setEmailError(null);
    setEmployeeError(null);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (a: AdminEmployeeListItem) => {
    setEditing(a);
    setForm({
      firstName: a.name.first ?? '',
      middleName: a.name.middle ?? '',
      lastName: a.name.last ?? '',
      email: a.email ?? '',
      altEmployeeID: a.altEmployeeID ?? '',
      employeeID: a.employeeID,
      adminEmployeeRoleID: a.adminEmployeeRoleID,
      employeeOnly: a.adminEmployeeRoleID === null,
    });
    setEmailError(null);
    setEmployeeError(null);
    setFormError(null);
    setModalOpen(true);
  };

  const closeModal = () => { if (!saving) setModalOpen(false); };

  // ── Derived modal state ───────────────────────────────────────────────────────
  const selectedRole = form.employeeOnly
    ? undefined
    : roles.find((r) => r.adminEmployeeRoleID === form.adminEmployeeRoleID);
  const selectedRoleIsSuper = !!selectedRole?.admin;
  const hasRoleChoice = form.employeeOnly || form.adminEmployeeRoleID !== null;
  // Employee-only access needs a linked employee (the API rejects it otherwise).
  const missingEmployeeLink = form.employeeOnly && !form.employeeID;

  // Client-side duplicate email check (server is authoritative; this is a fast mirror).
  const emailCollision = useMemo(() => {
    const e = form.email.trim().toLowerCase();
    if (!e) return false;
    return admins.some((a) =>
      (a.email ?? '').toLowerCase() === e &&
      (!editing || a.adminEmployeeID !== editing.adminEmployeeID));
  }, [form.email, admins, editing]);

  // Amber supervisor-scope-without-employee warning (recomputes on role/employee change).
  const showSupervisorWarning = selectedRole?.supervisor && !form.employeeID;

  // Last-super-admin demotion block (edit only): the edited admin is the last active
  // super admin and the new choice is a non-super-admin role or no role at all.
  const demotesLastSuperAdmin = !!editing
    && isLastActiveSuperAdmin(editing)
    && hasRoleChoice
    && !selectedRoleIsSuper;

  const canSubmit =
    form.firstName.trim() !== '' &&
    form.lastName.trim() !== '' &&
    form.email.trim() !== '' &&
    hasRoleChoice &&
    !missingEmployeeLink &&
    !emailCollision &&
    !demotesLastSuperAdmin;

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setEmailError(null);
    setEmployeeError(null);
    setFormError(null);
    const payload: AdminEmployeeWriteInput = {
      firstName: form.firstName.trim(),
      middleName: form.middleName.trim() || null,
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      altEmployeeID: form.altEmployeeID.trim() || null,
      employeeID: form.employeeID,
      adminEmployeeRoleID: form.employeeOnly ? null : form.adminEmployeeRoleID,
    };
    try {
      if (editing) await updateAdminEmployee(editing.adminEmployeeID, payload);
      else await createAdminEmployee(payload);
      setModalOpen(false);
      showToast(editing ? 'Admin updated.' : 'Admin added.');
      onReload();
    } catch (err) {
      const msg = getApiErrorMessage(err);
      // Route the known blocking messages to the field they are about.
      if (/email already exists/i.test(msg)) setEmailError(msg);
      else if (EMPLOYEE_LINK_ERROR_RE.test(msg)) setEmployeeError(msg);
      else setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Inactivate / reactivate ──────────────────────────────────────────────────
  const handleReactivate = async (a: AdminEmployeeListItem) => {
    try {
      await setAdminEmployeeActive(a.adminEmployeeID, true);
      showToast('Admin reactivated.');
      onReload();
    } catch (err) {
      showToast(getApiErrorMessage(err), true);
    }
  };

  const confirmInactivate = async () => {
    if (!confirmTarget) return;
    setConfirmBusy(true);
    try {
      await setAdminEmployeeActive(confirmTarget.adminEmployeeID, false);
      setConfirmTarget(null);
      showToast('Admin inactivated. Sign-in is now blocked.');
      onReload();
    } catch (err) {
      // Surface the server guard (e.g. last-super-admin / self) as a toast on the confirm.
      showToast(getApiErrorMessage(err), true);
      setConfirmTarget(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  // ── Reset sign-in (super admin; TAS-667) ─────────────────────────────────────
  const confirmResetSignIn = async () => {
    if (!resetTarget) return;
    setResetBusy(true);
    try {
      await resetAdminEmployeeSignIn(resetTarget.adminEmployeeID);
      setResetTarget(null);
      showToast('Sign-in reset. The next Microsoft sign-in with this email will be linked.');
      onReload();
    } catch (err) {
      showToast(getApiErrorMessage(err), true);
      setResetTarget(null);
    } finally {
      setResetBusy(false);
    }
  };

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onRoleSelect = (value: string) => {
    if (value === EMPLOYEE_ONLY_VALUE) {
      setForm((f) => ({ ...f, adminEmployeeRoleID: null, employeeOnly: true }));
    } else {
      setForm((f) => ({
        ...f,
        adminEmployeeRoleID: value === '' ? null : Number(value),
        employeeOnly: false,
      }));
    }
  };

  const roleSelectValue = form.employeeOnly
    ? EMPLOYEE_ONLY_VALUE
    : form.adminEmployeeRoleID === null ? '' : String(form.adminEmployeeRoleID);
  const roleSummaryText = form.employeeOnly ? EMPLOYEE_ONLY_SUMMARY : roleSummary(selectedRole);
  // Server link error wins; otherwise the client-side "required" hint.
  const employeeHint = employeeError ?? (missingEmployeeLink ? EMPLOYEE_LINK_REQUIRED_MSG : null);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Controls bar */}
      <div className="toolbar">
        <div className="field search">
          <span className="flab">Search</span>
          <div className="searchwrap">
            <Search />
            <input
              className="input"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="field">
          <span className="flab">Status</span>
          <select className="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All</option>
          </select>
        </div>
        <div className="field">
          <span className="flab">Role</span>
          <select
            className="select"
            value={roleFilter === 'all' || roleFilter === 'employeeOnly' ? roleFilter : String(roleFilter)}
            onChange={(e) => {
              const v = e.target.value;
              setRoleFilter(v === 'all' || v === 'employeeOnly' ? v : Number(v));
            }}
          >
            <option value="all">All roles</option>
            <option value="employeeOnly">Employee only (no admin role)</option>
            {roles.map((r) => (
              <option key={r.adminEmployeeRoleID} value={r.adminEmployeeRoleID}>{r.name}</option>
            ))}
          </select>
        </div>
        <div className="field push">
          <button className="btn primary" onClick={openAdd} type="button"><Plus />Add Admin</button>
        </div>
      </div>

      {/* Roster */}
      <div className="card">
        <div className="tbl-wrap">
          <table className="dt">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Linked Employee</th>
                <th className="cen">Delegations</th>
                <th className="cen">Status</th>
                <th className="cen">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty">
                      <div className="eic"><UserX /></div>
                      <div className="et">No admins match</div>
                      <p>Adjust the search or filters, or add a new admin.</p>
                    </div>
                  </td>
                </tr>
              ) : filtered.map((a) => {
                const linked = a.employeeID != null
                  ? (employeeName.get(a.employeeID) ?? `#${a.employeeID}`)
                  : null;
                const selfBlocked = isSelf(a);
                const lastSuper = isLastActiveSuperAdmin(a);
                const inactivateDisabled = selfBlocked || lastSuper;
                const tip = selfBlocked
                  ? "You can't inactivate your own account."
                  : lastSuper ? LAST_SUPER_ADMIN_MSG : undefined;
                return (
                  <tr key={a.adminEmployeeID} className={a.active ? '' : 'inactive-row'}>
                    <td>
                      <div className="who-cell">
                        <Avatar first={a.name.first} last={a.name.last} />
                        <div>
                          <div className="nm">
                            {a.name.first} {a.name.last}
                            {a.name.middle ? <span className="who-sub"> ({a.name.middle})</span> : null}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="email">{a.email}</span>
                      {typeof a.signedIn === 'boolean' && (
                        <div className={`signin-state${a.signedIn ? ' on' : ''}`}>
                          <span className="dot" />{a.signedIn ? 'Signed in' : 'Not yet signed in'}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="role-cell">
                        {a.adminEmployeeRoleID === null ? (
                          <span className="pill emp-only" title="No admin role: employee time portal access only">
                            Employee only
                          </span>
                        ) : (
                          <span>{a.roleName ?? <span className="dash">—</span>}</span>
                        )}
                        {a.roleIsSuperAdmin && <SuperAdminMarker />}
                      </div>
                    </td>
                    <td>{linked ? linked : <span className="dash">—</span>}</td>
                    <td className="cen">
                      <CountChip
                        count={a.delegationCount}
                        onClick={a.delegationCount ? () => onDelegationChip(a) : undefined}
                        title={a.delegationCount ? 'View delegations (coming soon)' : undefined}
                      />
                    </td>
                    <td className="cen">
                      <StatusPill variant={a.active ? 'active' : 'inactive'} />
                    </td>
                    <td className="cen">
                      <div className="row-act">
                        <button className="linkbtn" type="button" onClick={() => openEdit(a)}>
                          <Pencil size={14} />Edit
                        </button>
                        <span className="sep">·</span>
                        {/* Always rendered (disabled when unbound) so the column never changes width. */}
                        {a.signedIn ? (
                          <button className="linkbtn" type="button" onClick={() => setResetTarget(a)}>
                            Reset sign-in
                          </button>
                        ) : (
                          <span className="tipwrap" data-tip="Not signed in yet; nothing to reset.">
                            <button className="linkbtn" type="button" disabled>Reset sign-in</button>
                          </span>
                        )}
                        <span className="sep">·</span>
                        {a.active ? (
                          inactivateDisabled ? (
                            <span className="tipwrap" data-tip={tip}>
                              <button className="linkbtn danger" type="button" disabled>Inactivate</button>
                            </span>
                          ) : (
                            <button className="linkbtn danger" type="button" onClick={() => setConfirmTarget(a)}>
                              Inactivate
                            </button>
                          )
                        ) : (
                          <button className="linkbtn" type="button" onClick={() => handleReactivate(a)}>
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="rows-note"><b>{filtered.length}</b> of <b>{admins.length}</b> admins</div>

      {/* Add / Edit modal */}
      <ModalShell
        open={modalOpen}
        onClose={closeModal}
        wide
        icon={editing ? <Pencil /> : <UserPlus />}
        title={editing ? 'Edit admin' : 'Add admin'}
        subtitle={editing
          ? `${editing.name.first} ${editing.name.last}`.trim()
          : 'Authorize an existing Microsoft account to sign in to the admin app.'}
        footer={
          <>
            <button className="btn ghost" type="button" onClick={closeModal} disabled={saving}>Cancel</button>
            <button className="btn primary" type="button" onClick={handleSubmit} disabled={!canSubmit || saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add admin'}
            </button>
          </>
        }
      >
        <div className="modal-body">
          {formError && <ErrorBanner>{formError}</ErrorBanner>}
          {demotesLastSuperAdmin && <ErrorBanner>{LAST_SUPER_ADMIN_MSG}</ErrorBanner>}

          {/* Name row */}
          <div className="frow3">
            <div className="frow">
              <label>First name<span className="req">*</span></label>
              <input className="input" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
            </div>
            <div className="frow">
              <label>Middle <span className="opt">— optional</span></label>
              <input className="input" value={form.middleName} onChange={(e) => set('middleName', e.target.value)} />
            </div>
            <div className="frow">
              <label>Last name<span className="req">*</span></label>
              <input className="input" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
            </div>
          </div>

          {/* Email */}
          <div className="frow">
            <label>Email<span className="req">*</span></label>
            <input
              className={`input${(emailError || emailCollision) ? ' err' : ''}`}
              type="email"
              value={form.email}
              onChange={(e) => { set('email', e.target.value); setEmailError(null); }}
            />
            <div className="help">
              Must match the person's Microsoft sign-in email. This does not create the account — it authorizes an existing one.
            </div>
            {(emailError || emailCollision) && (
              <InlineError>{emailError ?? 'An admin with this email already exists.'}</InlineError>
            )}
          </div>

          {/* Alt Employee ID */}
          <div className="frow">
            <label>Alt Employee ID <span className="opt">— optional</span></label>
            <input className="input" value={form.altEmployeeID} onChange={(e) => set('altEmployeeID', e.target.value)} />
          </div>

          {/* Linked employee */}
          <div className="frow">
            <label>
              Linked employee
              {form.employeeOnly
                ? <span className="req">*</span>
                : <span className="opt"> — optional; required for supervisor scope</span>}
            </label>
            <SearchablePicker
              options={employeePickerOptions}
              value={form.employeeID}
              onChange={(id) => {
                set('employeeID', id === null ? null : Number(id));
                setEmployeeError(null);
              }}
              includeNone
              noneLabel="None (no linked employee)"
              placeholder="Search employees…"
            />
            {/* Fixed slot: the hint/error toggles visibility, never mounts. */}
            <div className="hint-slot" style={{ visibility: employeeHint ? 'visible' : 'hidden' }}>
              <InlineError>{employeeHint ?? EMPLOYEE_LINK_REQUIRED_MSG}</InlineError>
            </div>
          </div>

          {/* Role */}
          <div className="frow">
            <label>Role<span className="req">*</span></label>
            <select
              className="select"
              value={roleSelectValue}
              onChange={(e) => onRoleSelect(e.target.value)}
            >
              <option value="">Select a role…</option>
              <option value={EMPLOYEE_ONLY_VALUE}>No admin role — employee access only</option>
              {activeRoles.map((r) => (
                <option key={r.adminEmployeeRoleID} value={r.adminEmployeeRoleID}>{r.name}</option>
              ))}
            </select>
          </div>
          {/* Always mounted so picking a role doesn't push the content below it. */}
          <div className="role-summary" style={{ visibility: roleSummaryText ? 'visible' : 'hidden' }}>
            <Info />{roleSummaryText || 'No role selected.'}
          </div>

          {showSupervisorWarning && (
            <WarnBanner>
              This role uses supervisor scope, but no employee is linked. This admin won't see any
              employees until one is linked or a delegation is added.
            </WarnBanner>
          )}
        </div>
      </ModalShell>

      {/* Inactivate confirm (red) */}
      <ModalShell
        open={!!confirmTarget}
        onClose={() => { if (!confirmBusy) setConfirmTarget(null); }}
        variant="danger"
        icon={<AlertTriangle />}
        title="Inactivate admin?"
        subtitle={confirmTarget ? `${confirmTarget.name.first} ${confirmTarget.name.last}` : undefined}
        footer={
          <>
            <button className="btn ghost" type="button" onClick={() => setConfirmTarget(null)} disabled={confirmBusy}>Cancel</button>
            <button className="btn danger-solid" type="button" onClick={confirmInactivate} disabled={confirmBusy}>
              {confirmBusy ? 'Working…' : 'Inactivate admin'}
            </button>
          </>
        }
      >
        <div className="modal-confirm-body">
          Sign-in is blocked immediately. Their delegations are kept for audit but stop applying while the account is inactive.
        </div>
      </ModalShell>

      {/* Reset sign-in confirm (TAS-667) */}
      <ModalShell
        open={!!resetTarget}
        onClose={() => { if (!resetBusy) setResetTarget(null); }}
        icon={<RotateCcw />}
        title="Reset sign-in?"
        subtitle={resetTarget ? `${resetTarget.name.first} ${resetTarget.name.last}`.trim() : undefined}
        footer={
          <>
            <button className="btn ghost" type="button" onClick={() => setResetTarget(null)} disabled={resetBusy}>Cancel</button>
            <button
              className="btn primary"
              type="button"
              onClick={confirmResetSignIn}
              disabled={resetBusy}
              style={{ minWidth: 132 }}
            >
              {resetBusy ? 'Working…' : 'Reset sign-in'}
            </button>
          </>
        }
      >
        <div className="modal-confirm-body">
          This unlinks the Microsoft account that last signed in as <b>{resetTarget?.email}</b>. The next
          Microsoft sign-in with that email is linked instead. Use it when the mailbox was reassigned, or when
          the person is told their email is linked to a different Microsoft account.
        </div>
      </ModalShell>
    </>
  );
};

export default AdminEmployeesTab;
